import { request as http } from '@/utils/http'

// API基础路径
const API_BASE = '/mdm/feeManagement'

// 基础分页请求
export interface PageRequest {
  pageNum: number
  pageSize: number
}

// 分页结果
export interface PageResultVO<T> {
  records: T[]
  total: number
  pageNum: number
  pageSize: number
}

// 计费单位视图对象
export interface BillingUnitVO {
  id: string
  unitNameCn: string
  unitNameEn: string
  isContainerUnit: number
  enabled: number
  containerType?: string
}

// 费用管理分页查询请求
export interface FeeManagementQueryRequest extends PageRequest {
  tenantId?: string
  feeCode?: string
  feeNameCn?: string
  feeNameEn?: string
  currency?: string
  externalRelatedItem?: string
  externalRelationName?: string
  enabled?: number
  /** 运输业务类型筛选，枚举值与 ShokzTransportModeEnum 一致 */
  transportMode?: number
  keyword?: string
  isBasicFee?: number
  /** 费用类：港前、干线、尾程、全程（筛选条件，可选） */
  feeCategory?: string
  priceTypeIds?: number[]
}

// 费用管理视图对象
export interface FeeDefaultBillingUnits {
  fclDefaultBillingUnitId?: string
  lclDefaultBillingUnitId?: string
  airDefaultBillingUnitId?: string
  preportDefaultBillingUnitId?: string
  lastmileDefaultBillingUnitId?: string
  railDefaultBillingUnitId?: string
}

export interface FeeManagementVO extends FeeDefaultBillingUnits {
  id: string
  tenantId?: string
  createdBy?: string
  dateCreated?: string
  modifiedBy?: string
  dateModified?: string
  feeCode: string
  feeNameCn: string
  feeNameEn: string
  currency: string
  enabled: number
  isBasicFee?: number
  taxRate?: number
  /** 费用类：港前、干线、尾程、全程 */
  feeCategory?: string
  priceTypeIds?: number[]
  billingUnits?: BillingUnitVO[]
  feeType?: number
  /** 运输业务类型列表，枚举值与 ShokzTransportModeEnum 一致 */
  transportMode?: number[]
  externalRelatedItem: string
  externalRelationName?: string
}
// 费用管理保存请求
export interface FeeManagementSaveRequest extends FeeDefaultBillingUnits {
  id?: string
  tenantId?: string
  feeCode: string
  feeNameCn: string
  feeNameEn: string
  currency: string
  feeType?: number
  /** 运输业务类型列表，枚举值与 ShokzTransportModeEnum 一致 */
  transportMode?: number[]
  externalRelatedItem:string,
  externalRelationName?: string
  enabled: number
  isBasicFee?: number
  taxRate?: number
  /** 费用类：港前、干线、尾程、全程 */
  feeCategory?: string
  priceTypeIds?: number[]
  billingUnitIds?: string[]
}

// 费用计费单位关联关系查询请求
export interface FeeBillingUnitRelationQueryRequest extends PageRequest {
  feeId?: string
  billingUnitId?: string
  feeCode?: string
  feeNameCn?: string
  feeNameEn?: string
  billingUnitNameCn?: string
  billingUnitNameEn?: string
  tenantId?: string,
}

// 费用计费单位关联关系视图对象
export interface FeeBillingUnitRelationVO {
  id: string
  feeId: string
  billingUnitId: string
  feeCode?: string
  feeNameCn?: string
  feeNameEn?: string
  billingUnitNameCn?: string
  billingUnitNameEn?: string
  tenantId?: string
}

// 费用计费单位关联关系保存请求
export interface FeeBillingUnitRelationSaveRequest {
  id?: string
  feeId: string
  billingUnitId: string
  tenantId?: string
}

// 费用管理API
export const feeManagementApi = {
  // 分页查询费用管理列表
  page: (params: FeeManagementQueryRequest) => {
    return http.post<PageResultVO<FeeManagementVO>>(`${API_BASE}/queryPage`, params)
  },

  // 查询费用管理列表
  list: (params: FeeManagementQueryRequest) => {
    return http.post<FeeManagementVO[]>(`${API_BASE}/queryList`, params)
  },

  // 根据ID获取费用管理详情
  get: (id: string) => {
    return http.get<FeeManagementVO>(`${API_BASE}/getById`, null,{ params: { id } })
  },

  // 保存费用管理（新增或更新）
  save: (params: FeeManagementSaveRequest) => {
    return http.post<string | null>(`${API_BASE}/save`, params)
  },

  // 删除费用管理
  delete: (id: string) => {
    return http.delete<boolean>(`${API_BASE}/delete`, { params: { id } })
  },

  // 根据费用代码查询费用管理
  getByFeeCode: (feeCode: string, tenantId?: string) => {
    return http.post<FeeManagementVO>(`${API_BASE}/getByFeeCode`, null, {
      params: { feeCode, tenantId }
    })
  },

  // 模糊搜索费用管理
  search: (keyword: string, currency?: string, enabled?: number, tenantId?: string, transportMode?: number) => {
    return http.post<FeeManagementVO[]>(`${API_BASE}/search`, null, {
      params: { keyword, currency, enabled, tenantId, transportMode }
    })
  },

  // 批量删除费用管理
  batchDelete: (ids: string[]) => {
    return http.delete<boolean>(`${API_BASE}/batchDelete`, { data: ids })
  },

  // 批量启用费用管理
  batchEnable: (ids: string[]) => {
    return http.post<boolean>(`${API_BASE}/batchEnable`, ids)
  },

  // 批量禁用费用管理
  batchDisable: (ids: string[]) => {
    return http.post<boolean>(`${API_BASE}/batchDisable`, ids)
  },

  // 启用费用管理
  enable: (id: string) => {
    return http.post<boolean>(`${API_BASE}/enable`, null, { params: { id } })
  },

  // 禁用费用管理
  disable: (id: string) => {
    return http.post<boolean>(`${API_BASE}/disable`, null, { params: { id } })
  },

  // 批量更新状态
  updateStatus: (ids: string[], enabled: number) => {
    return http.post<boolean>(`${API_BASE}/updateStatus`, ids, { params: { enabled } })
  },

  // 根据币种查询费用管理列表
  queryByCurrency: (currency: string, tenantId?: string) => {
    return http.post<FeeManagementVO[]>(`${API_BASE}/queryByCurrency`, null, {
      params: { currency, tenantId }
    })
  },

  // 检查费用代码是否重复
  checkFeeCodeExists: (params: FeeManagementSaveRequest) => {
    return http.post<boolean>(`${API_BASE}/checkFeeCodeExists`, params)
  },

  // 查询基本费用列表
  queryBasicFees: (tenantId?: string) => {
    return http.post<FeeManagementVO[]>(`${API_BASE}/queryBasicFees`, null, {
      params: { tenantId }
    })
  },

  // 根据运价类型查询费用列表
  queryByPriceTypes: (priceTypeIds: number[], tenantId?: string) => {
    return http.post<FeeManagementVO[]>(`${API_BASE}/queryByPriceTypes`, priceTypeIds, {
      params: { tenantId }
    })
  },

  // 批量设置基本费用标识
  batchSetBasicFee: (ids: string[], isBasicFee: number) => {
    return http.post<boolean>(`${API_BASE}/batchSetBasicFee`, ids, { params: { isBasicFee } })
  }
}

// 费用计费单位关联关系API
export const feeBillingUnitRelationApi = {
  // 分页查询费用计费单位关联关系列表
  page: (params: FeeBillingUnitRelationQueryRequest) => {
    return http.post<PageResultVO<FeeBillingUnitRelationVO>>('/mdm/feeBillingUnitRelation/queryPage', params)
  },

  // 查询费用计费单位关联关系列表
  list: (params: FeeBillingUnitRelationQueryRequest) => {
    return http.post<FeeBillingUnitRelationVO[]>('/mdm/feeBillingUnitRelation/queryList', params)
  },

  // 根据ID获取费用计费单位关联关系详情
  get: (id: string) => {
    return http.get<FeeBillingUnitRelationVO>('/mdm/feeBillingUnitRelation/getById', { params: { id } })
  },

  // 保存费用计费单位关联关系（新增或更新）
  save: (params: FeeBillingUnitRelationSaveRequest) => {
    return http.post<boolean>('/mdm/feeBillingUnitRelation/save', params)
  },

  // 删除费用计费单位关联关系
  delete: (id: string) => {
    return http.post<boolean>('/mdm/feeBillingUnitRelation/delete', { params: { id } })
  },

  // 根据费用ID查询关联的计费单位列表
  queryByFeeId: (feeId: string, tenantId?: string) => {
    return http.post<FeeBillingUnitRelationVO[]>('/mdm/feeBillingUnitRelation/queryByFeeId', null, {
      params: { feeId, tenantId }
    })
  },

  // 根据计费单位ID查询关联的费用列表
  queryByBillingUnitId: (billingUnitId: string, tenantId?: string) => {
    return http.post<FeeBillingUnitRelationVO[]>('/mdm/feeBillingUnitRelation/queryByBillingUnitId', null, {
      params: { billingUnitId, tenantId }
    })
  },

  // 检查费用和计费单位是否已关联
  checkRelationExists: (feeId: string, billingUnitId: string, tenantId?: string) => {
    return http.post<boolean>('/mdm/feeBillingUnitRelation/checkRelationExists', null, {
      params: { feeId, billingUnitId, tenantId }
    })
  },

  // 批量保存费用计费单位关联关系
  batchSave: (requestList: FeeBillingUnitRelationSaveRequest[]) => {
    return http.post<boolean>('/mdm/feeBillingUnitRelation/batchSave', requestList)
  },

  // 批量删除费用计费单位关联关系
  batchDelete: (ids: string[]) => {
    return http.post<boolean>('/mdm/feeBillingUnitRelation/batchDelete', ids)
  },

  // 根据费用ID删除所有关联关系
  deleteByFeeId: (feeId: string, tenantId?: string) => {
    return http.post<boolean>('/mdm/feeBillingUnitRelation/deleteByFeeId', null, {
      params: { feeId, tenantId }
    })
  },

  // 根据计费单位ID删除所有关联关系
  deleteByBillingUnitId: (billingUnitId: string, tenantId?: string) => {
    return http.post<boolean>('/mdm/feeBillingUnitRelation/deleteByBillingUnitId', null, {
      params: { billingUnitId, tenantId }
    })
  }
}
